import { Container, Graphics, Sprite, Text } from "pixi.js"
import { TextRenderer } from "../text/text_renderer"
import { Callback } from "game-common/util"
import { COLORS, INTRO_FONT } from "./intro_theme"
import {
    easeOutCubic,
    easeOutExpo,
    letterStateAt,
    rampColor,
    ruleProgressAt,
    settleTs,
} from "./logo_choreography"

const LOGO_SIZE = 88
/** Wide tracking -- roughly 0.16em, applied between letters (never after the last one). */
const TRACKING = 14

/** Offset dark copy behind each glyph. The canopy underneath is busy and nearly the same value as
 *  the accent, so without this the wordmark has no edge to sit on. Matches drawPanel's shadow. */
const SHADOW_OFFSET_X = 2
const SHADOW_OFFSET_Y = 5
const SHADOW_ALPHA = 0.4

/** The halo is a scaled copy of the glyph behind it -- never a filter, see intro_theme. */
const GLOW_SPREAD = 1.07

/** The hairline under the wordmark: half-heights at its thickest point, in logical pixels. */
const RULE_WIDTH_RATIO = 0.56
const RULE_CORE_HALF = 1.0
const RULE_GLOW_HALF = 3.0
const RULE_GAP = 16

interface Letter {
    container: Container
    glyph: Text
    glow: Sprite
    shadow: Sprite
    width: number
}

export class ProjectLogo extends Graphics {
    logoText: string = "samsara"

    private letters: Letter[] = []
    private letterWidths: number[] = []
    private totalWidth: number = 0
    private lineHeight: number = 0

    private rule?: Graphics
    private startedAt: number = 0
    private pulsedAt?: number
    private frameHandle?: number
    private settleHandle?: any
    private disposed: boolean = false

    constructor() {
        super()
        this.build()
    }

    /**
     * Build every letter up front and cache the run's width.
     *
     * The width has to be exact from frame 1: `intro.ts` centres the wordmark with it once, and
     * the old version both hardcoded `calcWidth() => 630` against a run that rendered ~427 wide
     * AND appended letters one per timer tick, so the container grew while it was spelling itself
     * out and anything positioned from it drifted.
     */
    private build = () => {
        this.letters = this.logoText.split("").map(this.buildLetter)
        this.letterWidths = this.letters.map(letter => letter.width)
        this.lineHeight = this.letters.length > 0 ? this.letters[0].glyph.height : 0
        this.totalWidth =
            this.letterWidths.reduce((sum, w) => sum + w, 0) + TRACKING * Math.max(0, this.letterWidths.length - 1)
    }

    private renderLetter = (letter: string): Text => {
        const text = new TextRenderer()
            .fontFamily(INTRO_FONT)
            .fontSize(LOGO_SIZE)
            .color(0xffffff)
            // The default 1px black outline is for labels out in the world. Here it only muddies
            // the thin Jost 300 stems; the shadow copy below does the separation properly.
            .strokeThickness(0)
            .render(letter) as Text
        // Set before anything reads .width: a style change re-rasterizes, and Jost 300 and 400 have
        // different advances, so measuring at the default weight would mis-size the run.
        text.style.fontWeight = "300"
        return text
    }

    /**
     * One letter = three draws of the SAME raster. Reading `.width` forces the Text to rasterize
     * (the getter calls updateText), after which its texture can back plain Sprites for the shadow
     * and the halo -- 7 canvas textures for the wordmark instead of 21, and the two copies can
     * never drift out of register with the glyph.
     */
    private buildLetter = (letter: string): Letter => {
        const glyph = this.renderLetter(letter)
        glyph.anchor.set(0.5)
        // Reading .width is also what forces the raster -- the getter calls updateText -- and
        // everything below depends on the texture being final.
        const width = glyph.width

        const shadow = new Sprite(glyph.texture)
        shadow.anchor.set(0.5)
        shadow.tint = COLORS.shadow
        shadow.alpha = SHADOW_ALPHA
        shadow.x = SHADOW_OFFSET_X
        shadow.y = SHADOW_OFFSET_Y

        const glow = new Sprite(glyph.texture)
        glow.anchor.set(0.5)
        glow.tint = COLORS.accent
        glow.scale.set(GLOW_SPREAD)
        glow.alpha = 0

        const container = new Container()
        // Anchored glyphs mean the container's origin is the letter's centre, so the pop scales
        // about the letter rather than dragging it right and down.
        container.addChild(shadow, glow, glyph)

        return { container, glyph, glow, shadow, width }
    }

    calcWidth = () => this.totalWidth

    /** Where the hairline under the letters sits, in the wordmark's own coordinates. */
    ruleY = () => this.lineHeight + RULE_GAP

    /**
     * A one-off flare over the resting halo. `intro.ts` fires it as the block starts sliding up, so
     * the glow and the motion read as one event rather than two things that happen to overlap.
     */
    pulse = () => {
        this.pulsedAt = Date.now()
    }

    /**
     * Lay the wordmark out and start it. `callback` fires once the letters have landed -- that is
     * the screen's cue to slide the block up and bring in the login card.
     */
    update = (callback: Callback<void>) => {
        const root = new Container()

        let x = 0
        this.letters.forEach((letter, i) => {
            letter.container.x = x + this.letterWidths[i] / 2
            letter.container.y = this.lineHeight / 2
            root.addChild(letter.container)
            x += this.letterWidths[i] + TRACKING
        })

        this.rule = new Graphics()
        root.addChild(this.rule)

        this.startedAt = Date.now()
        this.applyFrame(0)

        // The letters are driven by animation frames, but the CUE is a timer. A hidden tab gets no
        // frames, and a player who opens the game in a background tab still has to find a login
        // card waiting when they come back -- so the cue never depends on anything having rendered.
        this.settleHandle = setTimeout(callback, settleTs(this.letters.length))
        this.frameHandle = requestAnimationFrame(this.tick)

        return root
    }

    private tick = () => {
        if (this.disposed) {
            return
        }
        this.applyFrame(Date.now() - this.startedAt)
        this.frameHandle = requestAnimationFrame(this.tick)
    }

    private applyFrame = (elapsed: number) => {
        const pulseElapsed = this.pulsedAt === undefined ? undefined : Date.now() - this.pulsedAt

        this.letters.forEach((letter, i) => {
            const state = letterStateAt(i, elapsed, pulseElapsed)
            letter.container.y = this.lineHeight / 2 + state.y
            letter.container.alpha = state.alpha
            letter.container.scale.set(state.scale)
            letter.glyph.tint = rampColor(COLORS.accent, COLORS.accentHi, COLORS.text, state.tintProgress)
            letter.glow.alpha = state.glowAlpha
        })

        this.drawRule(ruleProgressAt(elapsed, this.letters.length))
    }

    /**
     * The hairline wipes out from the centre.
     *
     * It is one lens-shaped path, not a bar and not a row of segments. A bar has two hard stops
     * and reads as a divider; segments with falling alpha leave a bright seam everywhere two of
     * them overlap. A lens tapers to nothing at both ends in a single fill, which is also the
     * cheaper draw -- and the same "shape as one continuous path" rule the chat bubbles follow.
     */
    private drawRule = (progress: number) => {
        if (!this.rule) {
            return
        }

        this.rule.clear()
        if (progress <= 0) {
            return
        }

        const halfWidth = (easeOutExpo(progress) * this.totalWidth * RULE_WIDTH_RATIO) / 2
        const alpha = easeOutCubic(progress)
        const cx = this.totalWidth / 2
        const y = this.lineHeight + RULE_GAP

        // A quadratic's peak sits halfway to its control point, so the control goes at twice the
        // half-height we actually want.
        const lens = (half: number) => {
            this.rule.moveTo(cx - halfWidth, y)
            this.rule.quadraticCurveTo(cx, y - half * 2, cx + halfWidth, y)
            this.rule.quadraticCurveTo(cx, y + half * 2, cx - halfWidth, y)
            this.rule.closePath()
        }

        // Fatter, dimmer copy for the glow, then the hairline itself. Never a filter -- intro_theme.
        this.rule.beginFill(COLORS.accent, 0.14 * alpha)
        lens(RULE_GLOW_HALF)
        this.rule.endFill()

        this.rule.beginFill(COLORS.accentHi, 0.9 * alpha)
        lens(RULE_CORE_HALF)
        this.rule.endFill()
    }

    /**
     * Stop the loop and free the rasters. A pixi Text's canvas texture is not reclaimed by GC, and
     * the login screen is torn down on every sign-in, so this has to be called -- see
     * `Intro.guestLandingRemover`.
     */
    dispose = () => {
        this.disposed = true
        if (this.frameHandle !== undefined) {
            cancelAnimationFrame(this.frameHandle)
            this.frameHandle = undefined
        }
        if (this.settleHandle !== undefined) {
            clearTimeout(this.settleHandle)
            this.settleHandle = undefined
        }

        this.letters.forEach(letter => {
            // The sprites borrow the glyph's texture, so only the glyph may destroy it.
            letter.shadow.destroy({ texture: false, baseTexture: false })
            letter.glow.destroy({ texture: false, baseTexture: false })
            letter.glyph.destroy({ texture: true, baseTexture: true })
            letter.container.destroy({ children: true })
        })
        this.letters = []

        this.rule?.destroy()
        this.rule = undefined
    }
}

